import React, {FC, useEffect, useMemo, useState} from "react";
import GatewayService from "../../service/GatewayService";
import {DropDownCompString, getKeyVal} from "../util/DropDownComp";
import {toast} from "react-hot-toast";
import {TimeUtil} from "../../util/TimeUtil";
import Calendar from "react-calendar";
import "../Calendar.css";
import {ClosingPosition, ExitRow, ReportResponse} from "../../models/ReportResponse";
import {ReportUploadResponse} from "../../models/ReportUploadResponse";
import {ClientReportRange} from "../../models/ClientReportRange";
import AppConstants from "../../util/AppConstants";

type CalendarField = "fromDate" | "toDate" | null;
type SortDirection = "asc" | "desc";

interface SortState<T> {
    key: keyof T;
    direction: SortDirection;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return JSON.stringify(value);
};

const Money: FC<{value: number | null | undefined}> = ({value}) => {
    const actual = value ?? 0;
    return <span>{actual.toLocaleString("en-IN", {style: "currency", currency: "INR", maximumFractionDigits: 2})}</span>;
};

const NumberCell: FC<{value: number | null | undefined}> = ({value}) => {
    if (value === null || value === undefined) {
        return <span>-</span>;
    }
    return <span>{value.toLocaleString("en-IN", {maximumFractionDigits: 2})}</span>;
};

const getSortIndicator = (active: boolean, direction: SortDirection) => {
    if (!active) {
        return " <>"
    }
    return direction === "asc" ? " ↑" : " ↓";
};

function compareValues(left: string | number | null | undefined, right: string | number | null | undefined) {
    if (left === right) {
        return 0;
    }
    if (left === null || left === undefined) {
        return 1;
    }
    if (right === null || right === undefined) {
        return -1;
    }
    if (typeof left === "number" && typeof right === "number") {
        return left - right;
    }
    return String(left).localeCompare(String(right));
}

const SortableHeader = <T,>({title, sortKey, sortState, onSort}: {
    title: string,
    sortKey: keyof T,
    sortState: SortState<T>,
    onSort: (key: keyof T) => void,
}) => {
    const isActive = sortState.key === sortKey;
    return <th
        onClick={() => onSort(sortKey)}
        style={{cursor: "pointer", userSelect: "none"}}
    >
        {title}{getSortIndicator(isActive, sortState.direction)}
    </th>;
};

const SummaryCard: FC<{title: string, children: React.ReactNode}> = ({title, children}) => {
    return <div className="positions-summary-card">
        <strong>{title}:</strong> {children}
    </div>;
};

const formatRangeDate = (value: string | null | undefined) => value?.trim() ? value : "-";

const ReportRangeSummary: FC<{range: ClientReportRange | null | undefined}> = ({range}) => {
    if (!range) {
        return <div>No uploaded report range found for the selected client.</div>;
    }

    return <div>
        Uploaded from <strong>{formatRangeDate(range.minDate)}</strong> to <strong>{formatRangeDate(range.maxDate)}</strong>
    </div>;
};

const UploadResult: FC<{data: unknown, emptyText: string}> = ({data, emptyText}) => {
    if (data === null || data === undefined) {
        return <div>{emptyText}</div>;
    }

    if (Array.isArray(data)) {
        if (data.length === 0) {
            return <div>No rows returned.</div>;
        }

        if (data.every(isRecord)) {
            const columns = Array.from(new Set(data.flatMap(row => Object.keys(row))));
            return <table className="margins-table">
                <thead>
                <tr>
                    {columns.map(column => <th key={column}>{column}</th>)}
                </tr>
                </thead>
                <tbody>
                {data.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {columns.map(column => <td key={column}>{formatCellValue(row[column])}</td>)}
                    </tr>
                ))}
                </tbody>
            </table>;
        }
    }

    if (isRecord(data)) {
        const entries = Object.entries(data);
        const primitiveOnly = entries.every(([, value]) => !Array.isArray(value) && !isRecord(value));
        if (primitiveOnly) {
            return <table className="margins-table">
                <thead>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                </thead>
                <tbody>
                {entries.map(([key, value]) => (
                    <tr key={key}>
                        <td>{key}</td>
                        <td>{formatCellValue(value)}</td>
                    </tr>
                ))}
                </tbody>
            </table>;
        }
    }

    return <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>{JSON.stringify(data, null, 2)}</pre>;
};

const ExitsTable: FC<{rows: ExitRow[]}> = ({rows}) => {
    const [collapsed, setCollapsed] = useState(true);
    const [sortState, setSortState] = useState<SortState<ExitRow>>({key: "exitTime", direction: "desc"});
    const totals = rows.reduce((acc, row) => {
        acc.quantity += row.quantity;
        acc.buyValue += row.buyValue;
        acc.sellValue += row.sellValue;
        acc.profit += row.profit;
        acc.turnover += row.turnover;
        acc.brokerage += row.brokerage;
        acc.otherCharges += row.otherCharges;
        return acc;
    }, {
        quantity: 0,
        buyValue: 0,
        sellValue: 0,
        profit: 0,
        turnover: 0,
        brokerage: 0,
        otherCharges: 0,
    });

    const sortedRows = useMemo(() => {
        return [...rows].sort((left, right) => {
            const comparison = compareValues(left[sortState.key], right[sortState.key]);
            return sortState.direction === "asc" ? comparison : -comparison;
        });
    }, [rows, sortState]);

    const rowsWithCumulativeProfit = useMemo(() => {
        let cumulativeProfit = 0;
        return sortedRows.map(row => {
            cumulativeProfit += row.profit;
            return {row, cumulativeProfit};
        });
    }, [sortedRows]);

    const updateSort = (key: keyof ExitRow) => {
        setSortState(current => current.key === key
            ? {key, direction: current.direction === "asc" ? "desc" : "asc"}
            : {key, direction: "asc"});
    };

    return <div className="control-group">
        <div style={{display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px"}}>
            <h3 style={{margin: 0}}>Exits ({rows.length})</h3>
            {rows.length > 0 && <button onClick={() => setCollapsed(value => !value)}>
                {collapsed ? "Expand" : "Collapse"}
            </button>}
        </div>
        {rows.length === 0 ? <div>No exits returned.</div> : collapsed ? <div>Collapsed</div> : <table className="margins-table">
            <thead>
            <tr>
                <th>S. No.</th>
                <SortableHeader<ExitRow> title="Symbol" sortKey="symbol" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Buy Time" sortKey="entryTime" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Sell Time" sortKey="exitTime" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Quantity" sortKey="quantity" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Buy Value" sortKey="buyValue" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Sell Value" sortKey="sellValue" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Profit" sortKey="profit" sortState={sortState} onSort={updateSort}/>
                <th>Cumulative Profit</th>
                <SortableHeader<ExitRow> title="Turnover" sortKey="turnover" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Brokerage" sortKey="brokerage" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ExitRow> title="Other Charges" sortKey="otherCharges" sortState={sortState} onSort={updateSort}/>
            </tr>
            </thead>
            <tbody>
            {rowsWithCumulativeProfit.map(({row, cumulativeProfit}, index) => (
                <tr key={`${row.symbol}-${row.entryTime}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{row.symbol}</td>
                    <td>{new Date(row.buyTime).toLocaleString()}</td>
                    <td>{new Date(row.sellTime).toLocaleString()}</td>
                    <td><NumberCell value={row.quantity}/></td>
                    <td><Money value={row.buyValue}/></td>
                    <td><Money value={row.sellValue}/></td>
                    <td><Money value={row.profit}/></td>
                    <td><Money value={cumulativeProfit}/></td>
                    <td><Money value={row.turnover}/></td>
                    <td><Money value={row.brokerage}/></td>
                    <td><Money value={row.otherCharges}/></td>
                </tr>
            ))}
            </tbody>
            <tfoot>
            <tr>
                <th colSpan={4} style={{textAlign: "right"}}>Totals</th>
                <th><NumberCell value={totals.quantity}/></th>
                <th><Money value={totals.buyValue}/></th>
                <th><Money value={totals.sellValue}/></th>
                <th><Money value={totals.profit}/></th>
                <th><Money value={totals.profit}/></th>
                <th><Money value={totals.turnover}/></th>
                <th><Money value={totals.brokerage}/></th>
                <th><Money value={totals.otherCharges}/></th>
            </tr>
            </tfoot>
        </table>}
    </div>;
};

const ClosingsTable: FC<{rows: ClosingPosition[]}> = ({rows}) => {
    const [sortState, setSortState] = useState<SortState<ClosingPosition>>({key: "unrealizedPnL", direction: "desc"});
    const totals = rows.reduce((acc, row) => {
        acc.remainingQuantity += row.remainingQuantity;
        acc.carryingValue += row.carryingValue;
        acc.unrealizedPnL += row.unrealizedPnL ?? 0;
        acc.remainingBrokerage += row.remainingBrokerage;
        acc.remainingOtherCharges += row.remainingOtherCharges;
        return acc;
    }, {
        remainingQuantity: 0,
        carryingValue: 0,
        unrealizedPnL: 0,
        remainingBrokerage: 0,
        remainingOtherCharges: 0,
    });

    const sortedRows = useMemo(() => {
        return [...rows].sort((left, right) => {
            const comparison = compareValues(left[sortState.key], right[sortState.key]);
            return sortState.direction === "asc" ? comparison : -comparison;
        });
    }, [rows, sortState]);

    const updateSort = (key: keyof ClosingPosition) => {
        setSortState(current => current.key === key
            ? {key, direction: current.direction === "asc" ? "desc" : "asc"}
            : {key, direction: "asc"});
    };

    return <div className="control-group">
        <h3>Closing Positions ({rows.length})</h3>
        {rows.length === 0 ? <div>No closing positions returned.</div> : <table className="margins-table">
            <thead>
            <tr>
                <SortableHeader<ClosingPosition> title="Symbol" sortKey="symbol" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="Side" sortKey="side" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="Remaining Qty" sortKey="remainingQuantity" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="FIFO Carry Price" sortKey="fifoCarryPrice" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="Carrying Value" sortKey="carryingValue" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="Current Price" sortKey="currentPrice" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="Unrealized PnL" sortKey="unrealizedPnL" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="Remaining Brokerage" sortKey="remainingBrokerage" sortState={sortState} onSort={updateSort}/>
                <SortableHeader<ClosingPosition> title="Remaining Other Charges" sortKey="remainingOtherCharges" sortState={sortState} onSort={updateSort}/>
            </tr>
            </thead>
            <tbody>
            {sortedRows.map((row, index) => (
                <tr key={`${row.symbol}-${row.side}-${index}`}>
                    <td>{row.symbol}</td>
                    <td>{row.side}</td>
                    <td><NumberCell value={row.remainingQuantity}/></td>
                    <td><Money value={row.fifoCarryPrice}/></td>
                    <td><Money value={row.carryingValue}/></td>
                    <td><Money value={row.currentPrice}/></td>
                    <td><Money value={row.unrealizedPnL}/></td>
                    <td><Money value={row.remainingBrokerage}/></td>
                    <td><Money value={row.remainingOtherCharges}/></td>
                </tr>
            ))}
            </tbody>
            <tfoot>
            <tr>
                <th colSpan={2} style={{textAlign: "right"}}>Totals</th>
                <th><NumberCell value={totals.remainingQuantity}/></th>
                <th></th>
                <th><Money value={totals.carryingValue}/></th>
                <th></th>
                <th><Money value={totals.unrealizedPnL}/></th>
                <th><Money value={totals.remainingBrokerage}/></th>
                <th><Money value={totals.remainingOtherCharges}/></th>
            </tr>
            </tfoot>
        </table>}
    </div>;
};

const ClientReportComponent: FC = () => {
    const clients = useMemo(() => ["RHN1309", "RHN1310", ...AppConstants.CLIENTS], []);
    // const {clients} = useClients();
    const [clientId, setClientId] = useState("");
    const [fromDate, setFromDate] = useState("01-04-2025");
    const [toDate, setToDate] = useState("31-03-2026");
    const [chain, setChain] = useState("");
    const [expiryDate, setExpiryDate] = useState("");
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarField, setCalendarField] = useState<CalendarField>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [reportData, setReportData] = useState<ReportResponse | null>(null);
    const [uploadResponse, setUploadResponse] = useState<ReportUploadResponse | null>(null);
    const [uploadedRanges, setUploadedRanges] = useState<ClientReportRange[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isLoadingRanges, setIsLoadingRanges] = useState(false);
    const [rangesError, setRangesError] = useState("");

    useEffect(() => {
        if (!clientId && clients.length > 0) {
            setClientId(clients[0]);
        }
    }, [clientId, clients]);

    const fetchUploadedRanges = async (silent = false) => {
        setIsLoadingRanges(true);
        setRangesError("");
        try {
            const response = await GatewayService.getUploadedReportRanges();
            const sortedRanges = [...response].sort((left, right) => left.clientId.localeCompare(right.clientId));
            setUploadedRanges(sortedRanges);
        } catch (error: any) {
            console.error(error);
            setUploadedRanges([]);
            setRangesError(error?.message || "Failed to load uploaded report ranges.");
            if (!silent) {
                toast.error(error?.message || "Failed to load uploaded report ranges.");
            }
        } finally {
            setIsLoadingRanges(false);
        }
    };

    useEffect(() => {
        fetchUploadedRanges(true);
    }, []);

    const selectedCalendarDate = useMemo(() => {
        const activeDate = calendarField === "toDate" ? toDate : fromDate;
        return TimeUtil.parseUIDate(activeDate) || new Date();
    }, [calendarField, fromDate, toDate]);

    const selectedClientRange = useMemo(
        () => uploadedRanges.find(range => range.clientId === clientId) || null,
        [uploadedRanges, clientId]
    );

    const isValidDate = (value: string) => value === "" || /^\d{2}-\d{2}-\d{4}$/.test(value);

    const fetchReport = async () => {
        if (!clientId) {
            toast.error("Please select a client.");
            return;
        }
        if (!isValidDate(fromDate) || !isValidDate(toDate) || !isValidDate(expiryDate)) {
            toast.error("Use DD-MM-YYYY for from/to/expiry date.");
            return;
        }

        setIsFetching(true);
        try {
            const response = await GatewayService.getReport(
                clientId,
                fromDate || undefined,
                toDate || undefined,
                chain.trim() || undefined,
                expiryDate || undefined
            );
            setReportData(response);
            toast.success("Report fetched.");
        } catch (error: any) {
            console.error(error);
            setReportData(null);
            toast.error(error?.message || "Failed to fetch report.");
        } finally {
            setIsFetching(false);
        }
    };

    const uploadFiles = async () => {
        if (!clientId) {
            toast.error("Please select a client.");
            return;
        }
        if (selectedFiles.length === 0) {
            toast.error("Select at least one file to upload.");
            return;
        }

        setIsUploading(true);
        try {
            const response = await GatewayService.uploadReport(clientId, selectedFiles);
            setUploadResponse(response);
            await fetchUploadedRanges(true);
            toast.success(
                `${response.clientId}: ${response.rowsInserted} rows inserted (${response.minDate} to ${response.maxDate})`,
                {duration: 8000}
            );
        } catch (error: any) {
            console.error(error);
            setUploadResponse(null);
            toast.error(error?.message || "Failed to upload report files.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDateChange = (newDate: Date | null | [Date | null, Date | null]) => {
        if (!(newDate instanceof Date) || !calendarField) {
            return;
        }
        const value = TimeUtil.toUIDate(newDate);
        if (calendarField === "fromDate") {
            setFromDate(value);
        } else {
            setToDate(value);
        }
        setShowCalendar(false);
        setCalendarField(null);
    };

    const toggleCalendar = (field: Exclude<CalendarField, null>) => {
        if (showCalendar && calendarField === field) {
            setShowCalendar(false);
            setCalendarField(null);
            return;
        }
        setCalendarField(field);
        setShowCalendar(true);
    };

    return <div className="container">
        <h2>Client Report</h2>
        <div className="control-group" style={{marginBottom: 12}}>
            <h3>Uploaded Report Coverage</h3>
            {isLoadingRanges ? <div>Loading uploaded report ranges...</div> : <ReportRangeSummary range={selectedClientRange}/>}
            {rangesError && <div style={{color: "red", marginTop: 8}}>{rangesError}</div>}
            {!isLoadingRanges && uploadedRanges.length > 0 && <table className="margins-table" style={{marginTop: 12}}>
                <thead>
                <tr>
                    <th>Client</th>
                    <th>Uploaded From</th>
                    <th>Uploaded To</th>
                </tr>
                </thead>
                <tbody>
                {uploadedRanges.map(range => (
                    <tr key={range.clientId} style={range.clientId === clientId ? {fontWeight: 700} : undefined}>
                        <td>{range.clientId}</td>
                        <td>{formatRangeDate(range.minDate)}</td>
                        <td>{formatRangeDate(range.maxDate)}</td>
                    </tr>
                ))}
                </tbody>
            </table>}
            {!isLoadingRanges && !rangesError && uploadedRanges.length === 0 && <div>No uploaded report ranges found yet.</div>}
        </div>
        <div className="positions-header">
            <div className="positions-controls">
                <div className="control-group">
                    <h3>Client</h3>
                    <DropDownCompString text={"Client"} actual={clientId} arr={getKeyVal(clients || [])} set={setClientId}/>
                </div>
                <div className="control-group">
                    <h3>Date Range</h3>
                    <input
                        type="text"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                        placeholder="From date (DD-MM-YYYY)"
                    />
                    <input
                        type="text"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                        placeholder="To date (DD-MM-YYYY)"
                    />
                    <div style={{display: "inline-flex", gap: "8px", flexWrap: "wrap"}}>
                        <button onClick={() => toggleCalendar("fromDate")}>
                            {showCalendar && calendarField === "fromDate" ? "Hide From Calendar" : "Pick From Date"}
                        </button>
                        <button onClick={() => toggleCalendar("toDate")}>
                            {showCalendar && calendarField === "toDate" ? "Hide To Calendar" : "Pick To Date"}
                        </button>
                    </div>
                    {showCalendar && <Calendar onChange={handleDateChange} value={selectedCalendarDate}/>}
                </div>
                <div className="control-group">
                    <h3>Chain</h3>
                    <input
                        type="text"
                        value={chain}
                        onChange={e => setChain(e.target.value)}
                        placeholder="Optional chain"
                    />
                </div>
                <div className="control-group">
                    <h3>Expiry Date</h3>
                    <input
                        type="text"
                        value={expiryDate}
                        onChange={e => setExpiryDate(e.target.value)}
                        placeholder="Optional expiry date (DD-MM-YYYY)"
                    />
                </div>
                <div className="control-group">
                    <h3>Upload Files</h3>
                    <input
                        type="file"
                        multiple
                        onChange={e => setSelectedFiles(Array.from(e.target.files || []))}
                    />
                    <div>{selectedFiles.length > 0 ? selectedFiles.map(file => file.name).join(", ") : "No files selected."}</div>
                </div>
                <div className="control-group">
                    <h3>Actions</h3>
                    <button onClick={fetchReport} disabled={isFetching || isUploading}>
                        {isFetching ? "Fetching..." : "Fetch Report"}
                    </button>
                    <button onClick={uploadFiles} disabled={isFetching || isUploading || selectedFiles.length === 0}>
                        {isUploading ? "Uploading..." : "Upload Report Files"}
                    </button>
                    <button onClick={() => {
                        setReportData(null);
                        setUploadResponse(null);
                        setSelectedFiles([]);
                    }} disabled={isFetching || isUploading}>
                        Clear Results
                    </button>
                </div>
            </div>
            <div className="expiry-filters">
                <h3>Fetched Report</h3>
                {isFetching && <div>Loading report...</div>}
                {!isFetching && !reportData && <div>Fetch a report to see results.</div>}
                {!isFetching && reportData && <>
                    <div className="control-group">
                        <h3>Realized Summary</h3>
                        <div className="positions-summary-row">
                            <SummaryCard title="Gross Profit"><Money value={reportData.realizedSummary.grossProfit}/></SummaryCard>
                            <SummaryCard title="Gross Loss"><Money value={reportData.realizedSummary.grossLoss}/></SummaryCard>
                            <SummaryCard title="Net Realized"><Money value={reportData.realizedSummary.netRealized}/></SummaryCard>
                            <SummaryCard title="Turnover"><Money value={reportData.realizedSummary.turnover}/></SummaryCard>
                            <SummaryCard title="Brokerage"><Money value={reportData.realizedSummary.brokerage}/></SummaryCard>
                            <SummaryCard title="Other Charges"><Money value={reportData.realizedSummary.otherCharges}/></SummaryCard>
                        </div>
                    </div>
                    <div className="control-group">
                        <h3>Closing Summary</h3>
                        <div className="positions-summary-row">
                            <SummaryCard title="Open Long Positions">{reportData.closingSummary.openLongPositions}</SummaryCard>
                            <SummaryCard title="Open Short Positions">{reportData.closingSummary.openShortPositions}</SummaryCard>
                            <SummaryCard title="Carrying Value"><Money value={reportData.closingSummary.carryingValue}/></SummaryCard>
                            <SummaryCard title="Unrealized PnL"><Money value={reportData.closingSummary.unrealizedPnL}/></SummaryCard>
                        </div>
                    </div>
                    <ExitsTable rows={reportData.exits}/>
                    <ClosingsTable rows={reportData.closings}/>
                </>}
            </div>
        </div>
        <div className="control-group" style={{marginTop: 12}}>
            <h3>Upload Response</h3>
            {isUploading ? <div>Uploading files...</div> : <UploadResult data={uploadResponse} emptyText={"Upload response will appear here."}/>}
        </div>
    </div>;
};

export default ClientReportComponent;
